import { Button } from '@socialflows/shared-lib';
import { DrawerClose, DrawerHeader, DrawerTitle, DrawerTrigger } from '@socialflows/shared-lib';
import { Input } from '@socialflows/shared-lib';
import { VerticalDrawer, VerticalDrawerContent } from '@socialflows/shared-lib';
import { LogIn, User, UserPlus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import GoogleIcon from '@/icons/google.svg';
import FacebookIcon from '/logos/facebook.svg';
import InstagramIcon from '/logos/instagram.svg';

import { useState } from 'react';
import { useAuth } from '@/contexts/auth-context';

export const AuthDrawer = () => {
  const { login, register } = useAuth();
  const [isRegisterAction, setIsRegisterAction] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    setError(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await login({
        email: formData.email,
        password: formData.password,
      });
      // Drawer will close automatically, user menu will update
    } catch (err: unknown) {
      setError((err as Error).message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });
      setSuccess('Registration successful! Please check your email to confirm your account.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
      });
    } catch (err: unknown) {
      setError((err as Error).message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <VerticalDrawer>
      <DrawerTrigger>
        <div className="relative p-2 text-foreground/80 hover:text-foreground transition-colors duration-200">
          <User className="h-5 w-5" strokeWidth={1.5} />
        </div>
      </DrawerTrigger>
      <VerticalDrawerContent>
        <DrawerHeader className="px-4 border-b">
          <DrawerTitle>
            <div className="flex items-center uppercase">
              {isRegisterAction ? 'Register' : 'Login'}
              <DrawerClose className="cursor-pointer ml-auto mr-0">
                <X className="w-6 h-6 text-gray-500 hover:text-black" />
              </DrawerClose>
            </div>
          </DrawerTitle>
        </DrawerHeader>
        <form
          onSubmit={isRegisterAction ? handleRegister : handleLogin}
          className="p-4 flex flex-col gap-2"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {success}
            </div>
          )}

          {isRegisterAction && (
            <>
              <Input
                className="h-12"
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
              <Input
                className="h-12"
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
              <Input
                className="h-12"
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </>
          )}
          <Input
            className="h-12"
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
          <Input
            className="h-12"
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleInputChange}
            required
          />
          {!isRegisterAction && (
            <Link to="#" className="text-sm text-gray-500 underline">
              Forgot your password?
            </Link>
          )}

          {!isRegisterAction && (
            <Button
              type="submit"
              className="rounded-md w-full text-md mt-4 py-5"
              variant="default"
              disabled={isLoading}
            >
              <LogIn className="h-5 w-5 mr-3" />
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
          )}

          {isRegisterAction && (
            <Button
              type="submit"
              className="rounded-md w-full text-md mt-4 py-5"
              variant="default"
              disabled={isLoading}
            >
              <UserPlus className="h-5 w-5 mr-2" />
              {isLoading ? 'Registering...' : 'Register'}
            </Button>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className="rounded-md w-full shadow bg-white border pr-8"
              variant="secondary"
            >
              <img src={GoogleIcon} className="w-5 h-5 mr-2" alt="Google" />
              Continue With Google
            </Button>

            <Button
              type="button"
              className="rounded-md w-full shadow bg-white border"
              variant="secondary"
            >
              <img src={FacebookIcon} className="w-5 h-5 mr-2" alt="Facebook" />
              Continue With Facebook
            </Button>

            <Button
              type="button"
              className="rounded-md w-full shadow bg-white border"
              variant="secondary"
            >
              <img src={InstagramIcon} className="w-5 h-5 mr-2" alt="Instagram" />
              Continue With Instagram
            </Button>
          </div>

          <hr />

          {isRegisterAction && (
            <Link
              to="#"
              className="text-sm text-gray-500 underline"
              onClick={(e) => {
                e.preventDefault();
                setIsRegisterAction(false);
                setError(null);
                setSuccess(null);
              }}
            >
              Already have an account? Login now
            </Link>
          )}

          {!isRegisterAction && (
            <Link
              to="#"
              className="text-sm text-gray-500 underline"
              onClick={(e) => {
                e.preventDefault();
                setIsRegisterAction(true);
                setError(null);
                setSuccess(null);
              }}
            >
              Don&apos;t have an account yet? Sign up now
            </Link>
          )}
        </form>
      </VerticalDrawerContent>
    </VerticalDrawer>
  );
};
